import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns'
import { forkJoin, from, map, Observable, switchMap, tap } from 'rxjs'

import {
  Environment,
  ExpandedHuntPackageRecord,
  HuntPackageRecord,
  HuntTemplate,
  HuntTemplateSummary,
  HUNT_PACKAGE_MAX,
} from '@cyborg-monorepo/hunter/common'
import { BaseDialogComponent } from '@hyperkit'

import { isHuntPackageHuntCompatible } from '../../../../../helpers/utils'
import { ContentService } from '../../../services/content.service'
import { HuntTemplateService } from '../../../services/hunt-template.service'

@Component({
  standalone: false,
  selector: 'add-to-template-dialog',
  templateUrl: './add-to-template-dialog.component.html',
  styleUrls: ['./add-to-template-dialog.component.scss'],
})
export class AddToTemplateDialogComponent
  extends BaseDialogComponent<
    { huntPackages: HuntPackageRecord[] },
    HuntTemplate
  >
  implements OnInit, AfterViewInit
{
  huntTemplateService = inject(HuntTemplateService)
  contentService = inject(ContentService)

  public formGroup: FormGroup
  public priorities: Array<string> = []
  public generatedHuntPackages: ExpandedHuntPackageRecord[]
  public static count = 0
  public error: string
  public huntTemplate = new FormControl()
  public huntTemplates: Array<HuntTemplateSummary>
  public huntTemplateData: Array<HuntTemplateSummary>
  @ViewChild('combo') public combo: ComboBoxComponent

  public ngOnInit(): void {
    this.huntTemplateService
      .getAllHuntTemplates()
      .subscribe((hunts: Array<HuntTemplateSummary>) => {
        this.huntTemplateData = this.huntTemplates = hunts
      })
  }

  public ngAfterViewInit(): void {
    const contains =
      (value) =>
      (s: HuntTemplateSummary): boolean =>
        s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1

    this.combo.filterChange
      .asObservable()
      .pipe(
        switchMap((value) =>
          from([this.huntTemplates]).pipe(
            tap(() => (this.combo.loading = true)),
            map((data) => data.filter(contains(value))),
          ),
        ),
      )
      .subscribe((x) => {
        this.huntTemplateData = x
        this.combo.loading = false
      })
  }

  public itemDisabled(itemArgs: {
    dataItem: HuntTemplateSummary
    index: number
  }): boolean {
    return (
      itemArgs.dataItem.numHuntPackages + AddToTemplateDialogComponent.count >
      HUNT_PACKAGE_MAX
    )
  }

  public onSubmit(): void {
    this.huntTemplateService
      .addHuntPackagesToTemplate(
        this.huntTemplate.value,
        this.generatedHuntPackages,
      )
      .subscribe(
        (hunt: HuntTemplate) => {
          this.dialogRef.close(hunt)
        },
        (error) => {
          this.error = error.message
        },
      )
  }

  public onSelectionChange(event): void {
    if (event.environments.length > 0) {
      const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
        this.data.huntPackages
          .filter((huntPackage) => {
            return isHuntPackageHuntCompatible(huntPackage)
          })
          .map((huntPackage) => {
            return this.contentService
              .getHuntPackageContent(huntPackage.UUID, {
                environments: event.environments.map((env) => env.id),
              })
              .pipe(
                map((generatedContent: Environment[]) => {
                  generatedContent = generatedContent.map((env) => {
                    env.tools = env.tools.filter((tool) => {
                      return (
                        tool.generatedContent &&
                        tool.generatedContent?.Hunt?.length > 0
                      )
                    })
                    return env
                  })

                  const expanded = huntPackage as ExpandedHuntPackageRecord
                  expanded.analysis = expanded.originalAnalysis =
                    generatedContent
                  return expanded
                }),
              )
          })
      forkJoin(generatedHuntPackages).subscribe((generatedHuntPacks) => {
        this.generatedHuntPackages = generatedHuntPacks
      })
    } else {
      this.generatedHuntPackages = this.data.huntPackages.filter(
        (huntPackage) => {
          return isHuntPackageHuntCompatible(huntPackage)
        },
      )
    }
  }

  public onClose(): void {
    this.dialogRef.close()
  }

  protected initializeOptions(): void {
    this.options = { headerText: 'Add To Template' }
  }
}
